import { Component, ElementRef, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { AssetTypeClassification, DamageReportDataDto, DamageReportRowDto, ReportsService } from 'src/shared/service-proxies/service-proxies';
import { PageHelperService } from '../helpers/page-helper.service';
import { PagebaseComponent } from '../pagebase/pagebase.component';
import { ApplicationStatusService } from '../services/application-status.service';
import { CustomerSettings } from '../services/customer-settings-service';
import { Router } from '@angular/router';
import { SessionStorageService } from '../services/session-storage.service';
import { VehicleTypeHelper } from '../helpers/VehicleTypeHelper';
import { DateTimeHelper } from '../helpers/datetime';
import moment from 'moment';

@Component({
  selector: 'app-asset-damage-report',
  templateUrl: './asset-damage-report.component.html',
  styleUrls: ['./asset-damage-report.component.scss'],
  standalone: false
})
export class AssetDamageReportComponent extends PagebaseComponent implements OnInit, OnDestroy {
  private static readonly STATE_KEY = 'asset-damage-report-state';
  @ViewChildren('containerSearchInput') private containerSearchInputs: QueryList<ElementRef<HTMLInputElement>>;

  customerId: string;
  data: DamageReportDataDto;

  private apiCall: Subscription;
  private filterChange$ = new Subject<void>();
  private destroy$ = new Subject<void>();

  loading: boolean = true;

  selectedContainerTypes: string[] = [];
  selectedDepotIds: string[] = [];
  selectedContainers: string[] = [];
  selectedStatuses: string[] = [];
  containerSearchTerm = '';

  containerOptions: string[] = [];
  statusOptions: string[] = [];

  totalDamaged = 0;
  selectedVehicleType: string;
  selectedRow: DamageReportRowDto | null = null;

  allRows: DamageReportRowDto[] = [];
  filteredRows: DamageReportRowDto[] = [];

  displayedColumns = ['depotName', 'assetId', 'assetType', 'locationName', 'status', 'fault', 'reportedDate', 'reportedBy', 'assessmentDate', 'vorTime', 'details', 'rowAction'];
  sortState: { active: string; direction: 'asc' | 'desc' } | null = null;
  private restoredState = false;
  private selectedRowAssetId: string | null = null;

  constructor(
    private _reportsService: ReportsService,
    private router: Router,
    private sessionStorageService: SessionStorageService,
    pageHelperService: PageHelperService,
    applicationStatusService: ApplicationStatusService,
    customerSettings: CustomerSettings
  ) {
    super(pageHelperService, applicationStatusService, customerSettings, 'Damage');
  }

  ngOnInit(): void {
    this.restoreState();
    this.filterChange$
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe(() => this.getData());
  }

  ngOnDestroy(): void {
    this.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
    this.filterChange$.complete();
  }

  unsubscribe(): void {
    if (this.apiCall) {
      this.apiCall.unsubscribe();
    }
  }

  reset(): void {
    this.data = null;
    this.totalDamaged = 0;
    this.selectedContainers = [];
    this.selectedStatuses = [];
    this.containerSearchTerm = '';
    this.containerOptions = [];
    this.statusOptions = [];
    this.selectedRow = null;
    this.selectedRowAssetId = null;
    this.allRows = [];
    this.filteredRows = [];

    if (this.apiCall) {
      this.apiCall.unsubscribe();
    }
  }

  private resetFiltersForCustomerChange(): void {
    this.selectedContainerTypes = [];
    this.selectedDepotIds = [];
    this.selectedContainers = [];
    this.selectedStatuses = [];
    this.containerSearchTerm = '';
    this.containerOptions = [];
    this.statusOptions = [];
    this.selectedRow = null;
    this.selectedRowAssetId = null;
    this.clearState();
  }

  getData(): void {
    if (!this.restoredState) {
      this.restoreState();
    }

    if (this.customerId && this.customerId != this.pageHelperService.selectedCompanyId) {
      this.resetFiltersForCustomerChange();
    }
    this.customerId = this.pageHelperService.selectedCompanyId;

    if (!this.customerId || this.customerId == '00000000-0000-0000-0000-000000000000') {
      return;
    }

    const storedVehicleType = this.sessionStorageService.getItem('dashboard-active-selectedVehicleType');
    if (storedVehicleType) {
      this.selectedVehicleType = JSON.parse(storedVehicleType);
    } else {
      this.selectedVehicleType = null;
    }

    this.loading = true;
    this.setIsLoading(this.loading);

    if (this.apiCall) {
      this.apiCall.unsubscribe();
    }

    this.apiCall = this._reportsService
      .getDamage(
        this.customerId,
        this.getAssetTypeClassification(this.selectedVehicleType),
        [],
        null,
        this.selectedContainerTypes.length ? this.selectedContainerTypes : [],
        null,
        [],
        null,
        this.selectedDepotIds.length ? this.selectedDepotIds : []
      )
      .subscribe(data => {
        this.data = data;
        this.allRows = [...(data?.rows ?? [])];
        this.buildLocalFilterOptions();
        this.refreshView();
        this.restoreSelectedRow();
        this.persistState();

        this.loading = false;
        this.setIsLoading(this.loading);
      }, error => {
        this.loading = false;
        this.setIsLoading(this.loading);
        console.error(error);
      });
  }

  onFilterChange(): void {
    this.persistState();
    this.filterChange$.next();
  }

  selectedVehicleTypeValueChange(newValue: string): void {
    if (newValue) {
      this.sessionStorageService.setItem('dashboard-active-selectedVehicleType', JSON.stringify(newValue));
    } else {
      this.sessionStorageService.removeItem('dashboard-active-selectedVehicleType');
    }

    this.selectedVehicleType = newValue;
    this.persistState();
    this.onFilterChange();
  }

  onLocalFilterChange(): void {
    this.refreshView();
    this.persistState();
  }

  get filteredContainerOptions(): string[] {
    return this.filterOptionsBySearch(this.containerOptions, this.containerSearchTerm, this.selectedContainers);
  }

  onContainerSelectOpenedChange(isOpen: boolean): void {
    if (isOpen) {
      this.focusContainerSearchInput();
      return;
    }

    this.containerSearchTerm = '';
  }

  onSearchKeydown(event: KeyboardEvent): void {
    event.stopPropagation();
  }

  private focusContainerSearchInput(): void {
    setTimeout(() => {
      const input = this.containerSearchInputs?.first?.nativeElement;
      input?.focus();
      input?.select();
    });
  }

  onRowClick(row: DamageReportRowDto): void {
    this.selectedRow = this.selectedRow === row ? null : row;
    this.selectedRowAssetId = this.selectedRow ? this.getAssetId(this.selectedRow) : null;
    this.persistState();
  }

  onRowDoubleClick(row: DamageReportRowDto): void {
    this.selectedRow = row;
    this.goToMoreInfo(row);
  }

  goToMoreInfo(row?: DamageReportRowDto): void {
    const targetRow = row ?? this.selectedRow;
    if (!targetRow) {
      return;
    }

    const assetId = this.getAssetId(targetRow);
    if (!assetId) {
      return;
    }

    this.selectedRow = targetRow;
    this.selectedRowAssetId = assetId;
    this.persistState();
    this.router.navigate(['/container-details', assetId]);
  }

  exportDamageReport(): void {
    this.loading = true;
    this.setIsLoading(this.loading);

    this.customerId = this.pageHelperService.selectedCompanyId;
    if (!this.customerId) {
      this.loading = false;
      this.setIsLoading(this.loading);
      return;
    }

    if (this.apiCall) {
      this.apiCall.unsubscribe();
    }

    this.apiCall = this._reportsService
      .exportDamage({
        customerId: this.customerId,
        group: this.getAssetTypeClassification(this.selectedVehicleType),
        selectedGroups: [],
        containerType: null,
        selectedContainerTypes: this.selectedContainerTypes.length ? this.selectedContainerTypes : [],
        locationType: null,
        selectedLocationTypes: [],
        depotId: null,
        selectedDepotIds: this.selectedDepotIds.length ? this.selectedDepotIds : []
      })
      .subscribe(data => {
        this.loading = false;
        this.setIsLoading(this.loading);
        const filename = data.fileName;
        const blob: Blob = data.data;
        const a = document.createElement('a');
        a.download = filename;
        a.href = window.URL.createObjectURL(blob);
        a.click();
      }, error => {
        this.loading = false;
        this.setIsLoading(this.loading);
        console.error(error);
      });
  }

  toggleSort(column: string): void {
    if (!this.sortState || this.sortState.active !== column) {
      this.sortState = { active: column, direction: 'asc' };
    } else if (this.sortState.direction === 'asc') {
      this.sortState = { active: column, direction: 'desc' };
    } else {
      this.sortState = null;
    }

    this.applySort();
    this.persistState();
  }

  getSortDirection(column: string): 'asc' | 'desc' | null {
    if (!this.sortState || this.sortState.active !== column) {
      return null;
    }
    return this.sortState.direction;
  }

  formatDate(value: any): string {
    return DateTimeHelper.formatDate(value);
  }

  formatVorTime(value: number | null | undefined): string {
    if (value == null) {
      return '';
    }

    return String(value);
  }

  getDamageDetailsUrl(row: DamageReportRowDto): string | null {
    const assetId = this.getAssetId(row);
    if (!assetId) {
      return null;
    }

    return `${window.location.origin}/damage/${assetId}`;
  }

  private refreshView(): void {
    const baseRows = this.allRows.filter(r =>
      (!this.selectedContainers.length || this.selectedContainers.includes(this.getContainerValue(r))) &&
      (!this.selectedStatuses.length || this.selectedStatuses.includes(this.normalizeLabel(r.status)))
    );

    this.filteredRows = baseRows;
    this.totalDamaged = this.filteredRows.length;
    this.applySort();

    if (this.selectedRow && !this.filteredRows.includes(this.selectedRow)) {
      this.selectedRow = null;
      this.selectedRowAssetId = null;
    }
  }

  private normalizeLabel(value: string | undefined | null): string {
    const trimmed = value?.trim();
    return trimmed ? trimmed : 'Unknown';
  }

  private getAssetTypeClassification(selectedVehicleType: string): AssetTypeClassification {
    return !selectedVehicleType ? AssetTypeClassification.All : VehicleTypeHelper.ConvertVehicleTypeNameToAssetTypeClassification(selectedVehicleType);
  }

  private getAssetId(row: DamageReportRowDto): string | null {
    const rowAsAny = row as any;
    return (rowAsAny?.assetId as string) ?? (rowAsAny?.containerId as string) ?? null;
  }

  getContainerValue(row: DamageReportRowDto): string {
    const assetId = this.getAssetId(row);
    return this.normalizeLabel(assetId);
  }

  private buildLocalFilterOptions(): void {
    const containers = new Set<string>();
    const statuses = new Set<string>();

    this.allRows.forEach(row => {
      containers.add(this.getContainerValue(row));
      statuses.add(this.normalizeLabel(row.status));
    });

    this.containerOptions = this.sortOptions(containers);
    this.statusOptions = this.sortOptions(statuses);

    this.selectedContainers = this.selectedContainers.filter(x => containers.has(x));
    this.selectedStatuses = this.selectedStatuses.filter(x => statuses.has(x));
  }

  private restoreSelectedRow(): void {
    if (!this.selectedRowAssetId) {
      this.selectedRow = null;
      return;
    }

    this.selectedRow = this.filteredRows.find(row => this.getAssetId(row) === this.selectedRowAssetId) ?? null;
    if (!this.selectedRow) {
      this.selectedRowAssetId = null;
    }
  }

  private restoreState(): void {
    const activeCustomerId = this.pageHelperService.selectedCompanyId;
    if (!activeCustomerId) {
      return;
    }

    this.restoredState = true;

    const rawState = this.sessionStorageService.getItem(AssetDamageReportComponent.STATE_KEY);
    if (!rawState) {
      return;
    }

    try {
      const state = JSON.parse(rawState);
      if (!state || state.customerId !== activeCustomerId) {
        return;
      }

      this.selectedVehicleType = state.selectedVehicleType ?? null;
      this.selectedContainerTypes = Array.isArray(state.selectedContainerTypes) ? state.selectedContainerTypes : [];
      this.selectedDepotIds = Array.isArray(state.selectedDepotIds) ? state.selectedDepotIds : [];
      this.selectedContainers = Array.isArray(state.selectedContainers) ? state.selectedContainers : [];
      this.selectedStatuses = Array.isArray(state.selectedStatuses) ? state.selectedStatuses : [];
      this.selectedRowAssetId = state.selectedRowAssetId ?? null;
      this.sortState = state.sortState ?? null;
    } catch {
      this.clearState();
    }
  }

  private persistState(): void {
    const customerId = this.pageHelperService.selectedCompanyId ?? this.customerId;
    if (!customerId || customerId === '00000000-0000-0000-0000-000000000000') {
      return;
    }

    this.sessionStorageService.setItem(
      AssetDamageReportComponent.STATE_KEY,
      JSON.stringify({
        customerId,
        selectedVehicleType: this.selectedVehicleType ?? null,
        selectedContainerTypes: this.selectedContainerTypes,
        selectedDepotIds: this.selectedDepotIds,
        selectedContainers: this.selectedContainers,
        selectedStatuses: this.selectedStatuses,
        selectedRowAssetId: this.selectedRowAssetId,
        sortState: this.sortState
      })
    );
  }

  private clearState(): void {
    this.sessionStorageService.removeItem(AssetDamageReportComponent.STATE_KEY);
  }

  private sortOptions(values: Set<string>): string[] {
    return Array.from(values).sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
  }

  private filterOptionsBySearch(options: string[], searchTerm: string, selectedValues: string[]): string[] {
    const term = searchTerm.trim().toLowerCase();
    if (!term) {
      return options;
    }

    return options.filter(option =>
      selectedValues.includes(option) || option.toLowerCase().includes(term)
    );
  }

  private applySort(): void {
    if (!this.filteredRows?.length || !this.sortState) {
      return;
    }

    const { active, direction } = this.sortState;
    const multiplier = direction === 'asc' ? 1 : -1;

    this.filteredRows = [...this.filteredRows].sort((a, b) => {
      const left = this.getSortValue(a, active);
      const right = this.getSortValue(b, active);

      if (left == null && right == null) {
        return 0;
      }
      if (left == null) {
        return 1;
      }
      if (right == null) {
        return -1;
      }

      if (typeof left === 'number' && typeof right === 'number') {
        return (left - right) * multiplier;
      }

      const leftString = String(left).toLowerCase();
      const rightString = String(right).toLowerCase();
      if (leftString < rightString) {
        return -1 * multiplier;
      }
      if (leftString > rightString) {
        return 1 * multiplier;
      }
      return 0;
    });
  }

  private getSortValue(row: DamageReportRowDto, column: string): string | number | null {
    switch (column) {
      case 'depotName':
        return row.depotName ?? null;
      case 'assetId':
        return this.getAssetId(row) ?? null;
      case 'assetType':
        return row.assetType ?? null;
      case 'locationName':
        return row.locationName ?? null;
      case 'status':
        return row.status ?? null;
      case 'fault':
        return row.fault ?? null;
      case 'reportedDate':
        return this.getDateSortValue(row.reportedDate);
      case 'reportedBy':
        return row.reportedBy ?? null;
      case 'assessmentDate':
        return this.getDateSortValue(row.assessmentDate);
      case 'vorTime':
        return row.vorTime ?? null;
      default:
        return null;
    }
  }

  private getDateSortValue(value: any): number | null {
    if (!value) {
      return null;
    }

    if (moment.isMoment(value)) {
      return value.valueOf();
    }

    const parsed = moment(value);
    return parsed.isValid() ? parsed.valueOf() : null;
  }
}
